import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Divider,
  IconButton,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { format } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useTranslation } from 'react-i18next';
import { useCollectionByFirm } from '../../hooks/useCollectionByFirm';
import { CommentSection } from '../../components';
import { formatCurrency } from '../../constants/tableConfigurations';
import { formatDateShort } from '../../utils/dateUtils';

const isReminderDue = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d <= today;
};

const isReminderToday = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  const d = new Date(dateStr);
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
};

const AmountChip = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ fontSize: 10, display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography fontWeight={800} sx={{ fontFamily: 'monospace', fontSize: 14, color }}>
      {value}
    </Typography>
  </Box>
);

const SectionHeader = ({ icon, title, onAdd, addTooltip, adding }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
    <Stack direction="row" alignItems="center" gap={1}>
      {icon}
      <Typography variant="body2" fontWeight={700} color="text.primary">{title}</Typography>
    </Stack>
    <Tooltip title={addTooltip}>
      <IconButton
        size="small"
        onClick={onAdd}
        sx={{
          color: adding ? 'primary.main' : 'text.secondary',
          bgcolor: adding ? alpha => `${alpha}18` : 'transparent',
          border: '1px dashed',
          borderColor: adding ? 'primary.main' : 'divider',
          borderRadius: 1.5,
          px: 1,
          py: 0.25,
          gap: 0.5,
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
        }}
      >
        <AddCircleOutlineIcon sx={{ fontSize: 15 }} />
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>Ekle</Typography>
      </IconButton>
    </Tooltip>
  </Stack>
);

const InlineForm = ({ children, onCancel, onSave, saving, disabled }) => (
  <Box sx={{ mb: 2, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2, bgcolor: theme => alpha(theme.palette.primary.main, 0.03) }}>
    <Stack gap={1.5}>
      {children}
      <Stack direction="row" gap={1} justifyContent="flex-end" pt={0.5}>
        <Button size="small" onClick={onCancel} sx={{ fontSize: 12, color: 'text.secondary' }}>İptal</Button>
        <Button size="small" variant="contained" onClick={onSave} disabled={disabled || saving} disableElevation sx={{ fontSize: 12, borderRadius: 1.5 }}>
          {saving ? <CircularProgress size={13} sx={{ color: 'white' }} /> : 'Kaydet'}
        </Button>
      </Stack>
    </Stack>
  </Box>
);

const ReminderItem = ({ reminder, onDelete }) => {
  const due = isReminderDue(reminder.reminder_date);
  const today = isReminderToday(reminder.reminder_date);
  const theme = useTheme();

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      p: 1.5, borderRadius: 2,
      border: '1px solid',
      borderColor: due ? alpha(theme.palette.warning.main, 0.4) : theme.palette.divider,
      bgcolor: due ? alpha(theme.palette.warning.main, 0.04) : 'transparent',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {due && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, width: 3, bottom: 0,
          bgcolor: today ? 'error.main' : 'warning.main',
          borderRadius: '2px 0 0 2px',
        }} />
      )}
      <Box sx={{ mt: 0.25 }}>
        {due
          ? <NotificationsActiveIcon sx={{ fontSize: 16, color: today ? 'error.main' : 'warning.main' }} />
          : <NotificationsNoneIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        }
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: 12, color: due ? (today ? 'error.main' : 'warning.main') : 'text.primary' }}>
            {formatDateShort(reminder.reminder_date)}
          </Typography>
          {today && (
            <Chip
              onClick = { ()=>{}}
              label="Bugün — Tahsilat zamanı!"
              size="small"
              icon={<WarningAmberIcon sx={{ fontSize: 11 }} />}
              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', border: 'none', '& .MuiChip-icon': { color: 'error.main' } }}
            />
          )}
          {due && !today && (
            <Chip
                          onClick = { ()=>{}}
              label="Tarihi geçti"
              size="small"
              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.dark', border: 'none' }}
            />
          )}
        </Stack>
        {reminder.note && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 12, mt: 0.25, lineHeight: 1.4 }}>
            {reminder.note}
          </Typography>
        )}
        {reminder.created_by_user && (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: 10.5, mt: 0.5 }}>
            {reminder.created_by_user.first_name} {reminder.created_by_user.last_name}
          </Typography>
        )}
      </Box>
      <Tooltip title="Sil">
        <IconButton size="small" onClick={() => onDelete(reminder.id)} sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const PromiseItem = ({ promise, onDelete }) => {
  const theme = useTheme();

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      p: 1.5, borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
    }}>
      <Box sx={{ mt: 0.25 }}>
        <HandshakeIcon sx={{ fontSize: 16, color: 'success.main' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontSize: 12 }}>
            {formatDateShort(promise.promise_date)}
          </Typography>
          {promise.amount != null && (
            <Chip
                          onClick = { ()=>{}}
              label={formatCurrency(promise.amount)}
              size="small"
              sx={{
                height: 18, fontSize: 11, fontFamily: 'monospace', fontWeight: 700,
                bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark', border: 'none',
              }}
            />
          )}
          {promise.promised_by && (
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: 12 }}>
              · {promise.promised_by}
            </Typography>
          )}
        </Stack>
        {promise.note && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 12, mt: 0.25, lineHeight: 1.4 }}>
            {promise.note}
          </Typography>
        )}
        {promise.created_by_user && (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: 10.5, mt: 0.5 }}>
            {promise.created_by_user.first_name} {promise.created_by_user.last_name}
          </Typography>
        )}
      </Box>
      <Tooltip title="Sil">
        <IconButton size="small" onClick={() => onDelete(promise.id)} sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const CollectionManageModal = ({ open, collection, onClose, onRefresh }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const firmId = collection?.firm_id;
  const { collection: data, loading, addReminder, deleteReminder, addPaymentPromise, deletePaymentPromise } = useCollectionByFirm(open ? firmId : null);

  const [showReminderForm, setShowReminderForm] = useState(false);
  const [showPromiseForm, setShowPromiseForm] = useState(false);

  const [reminderDate, setReminderDate] = useState(null);
  const [reminderNote, setReminderNote] = useState('');
  const [reminderSaving, setReminderSaving] = useState(false);

  const [promiseDate, setPromiseDate] = useState(null);
  const [promiseAmount, setPromiseAmount] = useState('');
  const [promisedBy, setPromisedBy] = useState('');
  const [promiseNote, setPromiseNote] = useState('');
  const [promiseSaving, setPromiseSaving] = useState(false);

  const handleAddReminder = async () => {
    if (!reminderDate) return;
    setReminderSaving(true);
    await addReminder({ reminder_date: format(reminderDate, 'yyyy-MM-dd'), note: reminderNote.trim() || null });
    setReminderSaving(false);
    setReminderDate(null);
    setReminderNote('');
    setShowReminderForm(false);
    onRefresh?.();
  };

  const handleDeleteReminder = async (id) => {
    await deleteReminder(id);
    onRefresh?.();
  };

  const handleAddPromise = async () => {
    if (!promiseDate) return;
    setPromiseSaving(true);
    await addPaymentPromise({
      promise_date: format(promiseDate, 'yyyy-MM-dd'),
      amount: promiseAmount ? parseFloat(promiseAmount) : null,
      promised_by: promisedBy.trim() || null,
      note: promiseNote.trim() || null,
    });
    setPromiseSaving(false);
    setPromiseDate(null);
    setPromiseAmount('');
    setPromisedBy('');
    setPromiseNote('');
    setShowPromiseForm(false);
    onRefresh?.();
  };

  const handleDeletePromise = async (id) => {
    await deletePaymentPromise(id);
    onRefresh?.();
  };

  const reminders = data?.reminders || [];
  const promises = data?.payment_promises || [];
  const dueCount = reminders.filter(r => isReminderDue(r.reminder_date)).length;

  const bakiyePos = data && Number(data.tl_bakiye) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{
        bgcolor: bakiyePos
          ? alpha(theme.palette.error.main, 0.06)
          : alpha(theme.palette.success.main, 0.06),
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 3, pt: 2.5, pb: 2,
      }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              bgcolor: bakiyePos ? alpha(theme.palette.error.main, 0.12) : alpha(theme.palette.success.main, 0.12),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AccountBalanceIcon sx={{ fontSize: 20, color: bakiyePos ? 'error.main' : 'success.main' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                {collection?.firm_name || '—'}
              </Typography>
              {collection?.firm_short_name && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                  {collection.firm_short_name}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Amount summary */}
        {data && (
          <Stack direction="row" gap={3} mt={2} divider={<Divider orientation="vertical" flexItem />}
            sx={{ bgcolor: 'background.paper', borderRadius: 2, px: 2, py: 1.5, border: `1px solid ${theme.palette.divider}` }}>
            <AmountChip label="TL Borç" value={formatCurrency(data.tl_bor)} color="error.main" />
            <AmountChip label="TL Alacak" value={formatCurrency(data.tl_alacak)} color="success.dark" />
            <AmountChip label="Net Bakiye" value={formatCurrency(data.tl_bakiye)} color={bakiyePos ? 'error.main' : 'success.dark'} />
            {dueCount > 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ fontSize: 10, display: 'block', mb: 0.25 }}>Hatırlatıcı</Typography>
                <Chip
                              onClick = { ()=>{}}
                  label={`${dueCount} vadesi geldi`}
                  size="small"
                  icon={<NotificationsActiveIcon sx={{ fontSize: 12 }} />}
                  sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.dark', border: 'none', '& .MuiChip-icon': { color: 'warning.dark' } }}
                />
              </Box>
            )}
          </Stack>
        )}
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
        ) : (
          <Stack gap={0}>
            {/* Reminders Section */}
            <SectionHeader
              icon={<NotificationsNoneIcon sx={{ fontSize: 18, color: dueCount > 0 ? 'warning.main' : 'text.secondary' }} />}
              title={t('tahsilat.reminders', 'Hatırlatıcılar')}
              onAdd={() => { setShowReminderForm(v => !v); setShowPromiseForm(false); }}
              addTooltip={t('tahsilat.reminder.add', 'Hatırlatıcı Ekle')}
              adding={showReminderForm}
            />

            {showReminderForm && (
              <InlineForm onCancel={() => setShowReminderForm(false)} onSave={handleAddReminder} saving={reminderSaving} disabled={!reminderDate}>
                <DatePicker
                  label="Tarih"
                  value={reminderDate}
                  onChange={setReminderDate}
                  format="dd.MM.yyyy"
                  views={['year', 'month', 'day']}
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { borderRadius: 1.5 } } } }}
                />
                <TextField
                  size="small"
                  label="Not (opsiyonel)"
                  value={reminderNote}
                  onChange={e => setReminderNote(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Bu firma ile ilgili hatırlatmak istediğiniz bir not..."
                  sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                />
              </InlineForm>
            )}

            {reminders.length === 0 && !showReminderForm ? (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 12 }}>
                  Henüz hatırlatıcı eklenmedi
                </Typography>
              </Box>
            ) : (
              <Stack gap={1} mb={2}>
                {reminders.map(r => (
                  <ReminderItem key={r.id} reminder={r} onDelete={handleDeleteReminder} />
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Payment Promises Section */}
            <SectionHeader
              icon={<HandshakeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
              title={t('tahsilat.paymentPromises', 'Ödeme Sözleri')}
              onAdd={() => { setShowPromiseForm(v => !v); setShowReminderForm(false); }}
              addTooltip={t('tahsilat.paymentPromise.add', 'Ödeme Sözü Ekle')}
              adding={showPromiseForm}
            />

            {showPromiseForm && (
              <InlineForm onCancel={() => setShowPromiseForm(false)} onSave={handleAddPromise} saving={promiseSaving} disabled={!promiseDate}>
                <Stack direction="row" gap={1.5}>
                  <DatePicker
                    label="Söz Tarihi"
                    value={promiseDate}
                    onChange={setPromiseDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { borderRadius: 1.5 } } } }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label="Tutar (opsiyonel)"
                    value={promiseAmount}
                    onChange={e => setPromiseAmount(e.target.value)}
                    fullWidth
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                  />
                </Stack>
                <TextField
                  size="small"
                  label="Söz Veren"
                  value={promisedBy}
                  onChange={e => setPromisedBy(e.target.value)}
                  fullWidth
                  placeholder="Ad Soyad veya unvan"
                  sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                />
                <TextField
                  size="small"
                  label="Not (opsiyonel)"
                  value={promiseNote}
                  onChange={e => setPromiseNote(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                />
              </InlineForm>
            )}

            {promises.length === 0 && !showPromiseForm ? (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 12 }}>
                  Henüz ödeme sözü eklenmedi
                </Typography>
              </Box>
            ) : (
              <Stack gap={1}>
                {promises.map(p => (
                  <PromiseItem key={p.id} promise={p} onDelete={handleDeletePromise} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
        </LocalizationProvider>

        {data && (
          <>
            <Divider sx={{ my: 2 }} />
            <CommentSection entityType="collection" entityId={data.id} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CollectionManageModal;
